import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _get from 'lodash/get';
import makeStyles from '@material-ui/styles/makeStyles';
import * as Types from 'pages/Features/tabs/Profile/TimezoneAndWorkHours/typings';
import useEntityTypes, { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';

import Autocomplete from '@material-ui/lab/Autocomplete';
import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';

interface LocationOption {
  value: Types.WorkLocation | null;
  label: string;
}

interface Props {
  className?: string;
  day: string;
  disabled?: boolean;
  location: Types.WorkLocation | null;
  onChange: (newItem: Types.WorkLocation | null) => void;
}

function LocationSelector({ className, day, disabled = false, location, onChange }: Props) {
  const classes = useStyles();
  const entityTypes = useEntityTypes();
  const userConfig = _get(entityTypes, PERSON_ENTITY_TYPE_KEY);
  const userConfigFields = _get(userConfig, '_fields', {});
  const options = useMemo(
    () =>
      _get(
        userConfigFields,
        ['timezoneAndWorkHours.workHours.location', 'validation', '_selectOptions'],
        [],
      ),
    [userConfigFields],
  ) as LocationOption[];

  const getInitialValue = useCallback(
    (newLocation?: Types.WorkLocation | null) => {
      return options.find(option => option.value === newLocation) || null;
    },
    [options],
  );

  const [locationValue, setLocationValue] = useState<LocationOption | null>(
    getInitialValue(location),
  );

  useEffect(() => {
    setLocationValue(getInitialValue(location));
  }, [location, getInitialValue]);

  const handleChange = (newValue: LocationOption | null) => {
    setLocationValue(newValue);
    onChange(_get(newValue, 'value', null));
  };

  return (
    <Autocomplete
      className={classNames(classes.root, className)}
      disabled={disabled}
      id={`${day}-location-selector`}
      getOptionLabel={(option: LocationOption) => option.label}
      getOptionSelected={(option: LocationOption, value: LocationOption) =>
        value.value === option.value
      }
      options={options}
      renderInput={(params: any) => (
        <TextField variant="outlined" placeholder="Work Location" {...params} />
      )}
      value={locationValue || null}
      onChange={(event: React.ChangeEvent<{}>, value: LocationOption | null) => handleChange(value)}
    />
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    minWidth: 225,
    marginLeft: theme.spacing(1),
  },
}));

export default LocationSelector;
